import { useCallback, useState } from 'react'

const buildStorageKey = (key: string) => `tableRowRestore:${key}`

export type TableRowRestoreState<TSortState> = {
    commentId: string
    tabId: string
    sortState: TSortState
    page: number
    rowsPerPage: number
    searchQuery: string
}

type UseTableRowRestoreResult<TSortState> = {
    restoreState: TableRowRestoreState<TSortState> | undefined
    scheduleRestore: (state: TableRowRestoreState<TSortState>) => void
    clearRestore: () => void
}

export const useTableRowRestore = <TSortState>(key: string): UseTableRowRestoreResult<TSortState> => {
    const storageKey = buildStorageKey(key)

    const [restoreState, setRestoreState] = useState<TableRowRestoreState<TSortState> | undefined>(() => {
        try {
            const raw = sessionStorage.getItem(storageKey)
            if (raw) {
                return JSON.parse(raw) as TableRowRestoreState<TSortState>
            }
        } catch {}
        return undefined
    })

    const scheduleRestore = useCallback(
        (state: TableRowRestoreState<TSortState>) => {
            try {
                sessionStorage.setItem(storageKey, JSON.stringify(state))
            } catch {}
        },
        [storageKey]
    )

    const clearRestore = useCallback(() => {
        try {
            sessionStorage.removeItem(storageKey)
        } catch {}
        setRestoreState(undefined)
    }, [storageKey])

    return { restoreState, scheduleRestore, clearRestore }
}
